import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Facebook, Instagram, Linkedin, Youtube, ArrowRight } from "lucide-react";
import { usePublicSettings } from "../contexts/PublicSettingsContext";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useCmsContent } from "../hooks/useCmsContent";
import { LanguageSwitcher } from "./LanguageSwitcher";

const DEFAULT_QUICK = [
  { key: "home", path: "/" }, { key: "courses", path: "/courses" },
  { key: "pricing", path: "/pricing" }, { key: "about", path: "/about" },
];
const DEFAULT_SUPPORT = [
  { key: "faq", path: "/faq" }, { key: "contact", path: "/contact" },
  { key: "levelTest", path: "/level-test" }, { key: "bookTrial", path: "/book-trial" },
];

export const Footer = () => {
  const { settings } = usePublicSettings();
  const { isAuthenticated, user } = useAuth();
  const { t } = useLanguage();
  const cms = useCmsContent('footer');

  const logoUrl     = cms.get('info', 'logo_url', '/logo.png');
  const description = cms.get('info', 'description', '');
  const location    = cms.get('info', 'location', t('footer.locationDefault'));

  const cmsQuick   = cms.getList('quick_links', []);
  const cmsSupport = cms.getList('support_links', []);
  const quickLinks   = cmsQuick.length   > 0 ? cmsQuick.map(i => ({ name: i.label, path: i.path }))   : DEFAULT_QUICK.map(item => ({ name: t(`nav.${item.key}`), path: item.path }));
  const supportLinks = cmsSupport.length > 0 ? cmsSupport.map(i => ({ name: i.label, path: i.path })) : DEFAULT_SUPPORT.map(item => ({ name: t(`nav.${item.key}`), path: item.path }));

  const legalLinks = [
    { name: t('footer.privacyPolicy'), path: "/privacy" },
    { name: t('footer.termsOfService'), path: "/terms" },
  ];

  const socialLinks = [
    {
      key: "facebook",
      href: settings.facebook_url,
      label: "Facebook",
      icon: Facebook,
      hoverClass: "hover:bg-[#D72B2B]",
      testId: "social-facebook",
    },
    {
      key: "instagram",
      href: settings.instagram_url,
      label: "Instagram",
      icon: Instagram,
      hoverClass: "hover:bg-[#F5A623]",
      testId: "social-instagram",
    },
    {
      key: "linkedin",
      href: settings.linkedin_url,
      label: "LinkedIn",
      icon: Linkedin,
      hoverClass: "hover:bg-[#4CAF50]",
      testId: "social-linkedin",
    },
    {
      key: "youtube",
      href: settings.youtube_url,
      label: "YouTube",
      icon: Youtube,
      hoverClass: "hover:bg-[#D72B2B]",
      testId: "social-youtube",
    },
  ].filter((link) => Boolean(link.href));

  const contactEmail = settings.contact_email || "franklytalkingenglish@gmail.com";
  const siteName = settings.site_name || "Frankly Talking English";
  const siteTagline = settings.site_tagline || "Speak English with Confidence";
  const studentAccessPath = !isAuthenticated
    ? "/login"
    : user?.role === "admin" || user?.role === "teacher"
      ? "/admin/dashboard"
      : "/student/dashboard";

  return (
    <footer className="bg-[#1A2E6E] text-white relative" data-testid="main-footer">
      <div className="h-1 w-full flex">
        <div className="flex-1 bg-[#D72B2B]"></div>
        <div className="flex-1 bg-[#4CAF50]"></div>
        <div className="flex-1 bg-[#F5A623]"></div>
      </div>

      <div className="container-custom py-14 sm:py-16">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          <div className="lg:col-span-1">
            <img
              src={logoUrl}
              alt={siteName}
              className="h-14 w-auto mb-4 brightness-0 invert"
              loading="lazy"
              width="180"
              height="56"
            />
            <p className="text-white/90 text-sm font-semibold leading-relaxed mb-2">
              FRANKLY TALKING ENGLISH - {t('footer.taglineSuffix')}
            </p>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {description || t('footer.descriptionDefault')}
            </p>
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-3">
                {socialLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <a
                      key={link.key}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white transition-all duration-200 focus:outline-2 focus:outline-[#D72B2B] focus:outline-offset-2 ${link.hoverClass}`}
                      aria-label={link.label}
                      data-testid={link.testId}
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <h4 className="font-sora font-semibold text-lg mb-5 text-[#F5A623]">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/80 hover:text-white hover:pl-2 transition-all duration-200 text-sm flex items-center gap-2 focus:outline-none focus:text-[#F5A623]"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-sora font-semibold text-lg mb-5 text-[#4CAF50]">
              {t('footer.support')}
            </h4>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-white/80 hover:text-white hover:pl-2 transition-all duration-200 text-sm focus:outline-none focus:text-[#F5A623]"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-sora font-semibold text-lg mb-5 text-[#D72B2B]">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-4">
              <li className="flex min-w-0 items-start gap-3">
                <Mail size={18} className="text-[#F5A623] mt-0.5 flex-shrink-0" />
                <a
                  href={`mailto:${contactEmail}`}
                  className="break-words text-sm text-white transition-colors duration-200 hover:text-[#F5A623] focus:outline-none focus:text-[#F5A623]"
                  data-testid="footer-email"
                >
                  {contactEmail}
                </a>
              </li>
              <li className="flex min-w-0 items-start gap-3">
                <MapPin size={18} className="text-[#4CAF50] mt-0.5 flex-shrink-0" />
                <span className="break-words text-sm text-white/80">{location}</span>
              </li>
            </ul>
            <Link
              to={studentAccessPath}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D72B2B] px-5 py-2.5 text-sm font-semibold text-white transition-all duration-200 hover:scale-103 hover:bg-[#B71C1C] focus:outline-2 focus:outline-white focus:outline-offset-2 sm:w-auto"
              data-testid="footer-student-access"
            >
              {t('footer.studentAccess')}
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-white/20 pt-8 text-center md:flex-row md:text-left">
          <p className="text-sm text-white/60">
            {t('footer.copyright', { year: new Date().getFullYear(), siteName })}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            {legalLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-white/60 hover:text-white transition-colors duration-200 text-sm focus:outline-none focus:text-[#F5A623]"
              >
                {link.name}
              </Link>
            ))}
            <LanguageSwitcher tone="dark" />
          </div>
        </div>

        <div className="border-t border-white/10 mt-6 pt-6 text-center">
          <a
            href="https://suddenly.com.br"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-white/45 hover:text-[#F5A623] transition-colors duration-200 focus:outline-none focus:text-[#F5A623]"
            data-testid="developer-credit"
          >
            {t('footer.developerCredit')}
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
